import React, { createContext, useState, useEffect, useContext } from "react";
import {
  getAuth,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getFirestore, getDoc } from "firebase/firestore";
import firebaseApp from "../firebase"; // Your firebase config

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const auth = getAuth(firebaseApp);
  const db = getFirestore(firebaseApp);

  useEffect(() => {
    const configurePersistence = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch (error) {
        console.error("Error setting persistence:", error);
      }
    };

    configurePersistence();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Admin identity = server-granted custom claim only (never Firestore fields).
          const tokenResult = await user.getIdTokenResult();
          if (tokenResult.claims.admin !== true) {
            setCurrentUser(null);
            await signOut(auth);
          } else {
            let userData = {};
            try {
              const userDocSnap = await getDoc(doc(db, "users", user.uid));
              if (userDocSnap.exists()) userData = userDocSnap.data();
            } catch (e) {
              console.error("Profile read failed:", e.code);
            }
            // isAdmin is set last so profile fields cannot override the claim result.
            setCurrentUser({ ...user, ...userData, uid: user.uid, email: user.email, isAdmin: true });
          }
        } catch (e) {
          console.error("Token check failed:", e.code);
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [auth, db]);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Force refresh so a freshly granted claim is picked up.
      const tokenResult = await user.getIdTokenResult(true);
      if (tokenResult.claims.admin !== true) {
        await signOut(auth);
        throw new Error(
          "Access denied. This account does not have admin access yet. If you are staff, the admin claim has not been granted for this account - please ask the owner to grant it, then sign in again."
        );
      }

      let userData = {};
      try {
        const userDocSnap = await getDoc(doc(db, "users", user.uid));
        if (userDocSnap.exists()) userData = userDocSnap.data();
      } catch (e) {
        console.error("Profile read failed:", e.code);
      }
      setCurrentUser({ ...user, ...userData, uid: user.uid, email: user.email, isAdmin: true });
      return { email: user.email, role: "admin" };
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = () => signOut(auth);

  const value = {
    currentUser,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
